// Shared helper to await the final state of the AI suggestions generation.
// Used to enrich Slack notifications with the list of suggested venues
// regardless of which flow triggered the brief completion (LeadFormModal, email-only follow-up).

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export interface SuggestionItem {
  name?: string;
  [key: string]: any;
}

export interface WaitForSuggestionsOptions {
  /** Maximum wait time in ms. Default: 90s — generation can take up to ~60s. */
  maxWaitMs?: number;
  /** Polling interval in ms. Default: 1500ms. */
  pollIntervalMs?: number;
}

export interface WaitForSuggestionsResult {
  status: "completed" | "failed" | "timeout" | "error";
  suggestions: SuggestionItem[];
}

/**
 * Polls `check-suggestions-status` until a terminal state is reached
 * (`completed`, `failed`) or the timeout is exhausted.
 * Constraint: GET request without `Content-Type` header.
 */
export async function waitForSuggestions(
  sessionId: string,
  options: WaitForSuggestionsOptions = {}
): Promise<WaitForSuggestionsResult> {
  const maxWaitMs = options.maxWaitMs ?? 90_000;
  const pollIntervalMs = options.pollIntervalMs ?? 1500;
  const start = Date.now();

  while (Date.now() - start < maxWaitMs) {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/check-suggestions-status?session_id=${encodeURIComponent(sessionId)}`
      );
      if (res.ok) {
        const data = await res.json();
        if (data.status === "completed" && Array.isArray(data.suggestions)) {
          return { status: "completed", suggestions: data.suggestions };
        }
        if (data.status === "failed") {
          return { status: "failed", suggestions: [] };
        }
      }
    } catch (e) {
      console.warn("[waitForSuggestions] poll error:", e);
    }
    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }

  return { status: "timeout", suggestions: [] };
}

/** Build the Slack `*Suggestions proposées*` line from a suggestions array. */
export function buildSuggestionsSlackLine(suggestions: SuggestionItem[]): string {
  const names = suggestions.map((s) => s?.name).filter(Boolean).join(", ");
  return names ? `\n*Suggestions proposées* : ${names}` : "";
}
